import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BehaviourType } from "./types";
import { createBaseSelector, registerSlice } from "shared/lib/redux";
import { ValueOf } from "shared/lib/typeCheck.lib";

type AppState = {
  isPanelVisible: boolean;
  appIntegrationRequestLimitReached: boolean;
  isMarketplaceVisible: boolean;
  appIntegrationHasApiAccess: boolean;
  webAppRequestLimitReached: boolean;
  requestedFromUrl: boolean;

  // modals
  isDeleteModalOpened: boolean;
  isUploadFileModalOpened: boolean;
  isBugTrackerModalOpened: boolean;

  // app feature/UI toggle
  appConfig: {
    isCardSearchVisible: boolean;
    isCardShippingLineVisible: boolean;
    urlBehaviour: ValueOf<typeof BehaviourType>;
  };
};

const initialAppState: AppState = {
  isPanelVisible: true,
  webAppRequestLimitReached: false,
  isMarketplaceVisible: false,
  appIntegrationRequestLimitReached: false,
  appIntegrationHasApiAccess: true,
  requestedFromUrl: false,
  // modals
  isDeleteModalOpened: false,
  isUploadFileModalOpened: false,
  isBugTrackerModalOpened: false,

  // app feature/UI toggle
  appConfig: {
    isCardSearchVisible: false,
    isCardShippingLineVisible: false,
    urlBehaviour: BehaviourType.Query,
  },
};

const appSlice = createSlice({
  name: "app",
  initialState: initialAppState,
  reducers: {
    setIsPanelVisible: (state, action: PayloadAction<boolean>) => {
      state.isPanelVisible = action.payload;
    },
    setIsCardSearchVisibleAppConfig: (state, action: PayloadAction<boolean>) => {
      state.appConfig.isCardSearchVisible = action.payload;
    },
    setIsCardShippingLineVisibleAppConfig: (state, action: PayloadAction<boolean>) => {
      state.appConfig.isCardShippingLineVisible = action.payload;
    },
    setRequestedFromUrl: (state, action: PayloadAction<boolean>) => {
      state.requestedFromUrl = action.payload;
    },

    // modal actions
    openDeleteCardModalAction: (state) => {
      state.isDeleteModalOpened = true;
    },
    closeDeleteModalAction: (state) => {
      state.isDeleteModalOpened = false;
    },
    openUploadFileModalAction: (state) => {
      state.isUploadFileModalOpened = true;
    },
    closeUploadFileModalAction: (state) => {
      state.isUploadFileModalOpened = false;
    },
    setBugTrackerModalOpened: (state, action: PayloadAction<boolean>) => {
      state.isBugTrackerModalOpened = action.payload;
    },

    setWebAppRequestLimitReached: (state, action: PayloadAction<boolean>) => {
      state.webAppRequestLimitReached = action.payload;
    },
    setAppIntegrationRequestLimitReached: (state, action: PayloadAction<boolean>) => {
      state.appIntegrationRequestLimitReached = action.payload;
    },
    setAppIntegrationHasApiAccess: (state, action: PayloadAction<boolean>) => {
      state.appIntegrationHasApiAccess = action.payload;
    },
    setUrlBehaviour: (state, action: PayloadAction<ValueOf<typeof BehaviourType>>) => {
      state.appConfig.urlBehaviour = action.payload;
    },
    setIsMarketplaceVisible: (state, action: PayloadAction<boolean>) => {
      state.isMarketplaceVisible = action.payload;
    },
  },
});

const appBaseSelector = createBaseSelector(appSlice);

registerSlice([appSlice]);

export const appStore = {
  actions: appSlice.actions,
  selectors: {
    isPanelVisible: createSelector(appBaseSelector, (s) => s.isPanelVisible),
    webAppRequestLimitReached: createSelector(appBaseSelector, (s) => s.webAppRequestLimitReached),
    appIntegrationRequestLimitReached: createSelector(appBaseSelector, (s) => s.appIntegrationRequestLimitReached),
    appIntegrationHasApiAccess: createSelector(appBaseSelector, (s) => s.appIntegrationHasApiAccess),
    requestedFromUrl: createSelector(appBaseSelector, (s) => s.requestedFromUrl),
    isMarketplaceVisible: createSelector(appBaseSelector, (s) => s.isMarketplaceVisible),
    // modals
    isDeleteModalOpened: createSelector(appBaseSelector, (s) => s.isDeleteModalOpened),
    isUploadFileModalOpened: createSelector(appBaseSelector, (s) => s.isUploadFileModalOpened),
    isBugTrackerModalOpened: createSelector(appBaseSelector, (s) => s.isBugTrackerModalOpened),

    // app feature/UI toggle
    urlBehaviour: createSelector(appBaseSelector, (s) => s.appConfig.urlBehaviour),
    isCardSearchVisible: createSelector(appBaseSelector, (s) => s.appConfig.isCardSearchVisible),
    isCardShippingLineVisible: createSelector(appBaseSelector, (s) => s.appConfig.isCardShippingLineVisible),
  },
};
